import * as React from 'react';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { StyleProp, ViewStyle } from 'react-native';

import useOrderNumber from 'hooks/use-order-number';
import { useLocale } from 'state/intl';
import { useOrderTimedFire } from 'state/order/hooks/use-order-timed-fire';
import { format } from 'utils/language/format-date';
import { getLocalizedServiceMode } from 'utils/service-mode';

import {
  ReceiptDate,
  ReceiptHeadInfoContainer,
  ReceiptInfo,
  ReceiptInfoDetail,
  ReceiptInfoDetailLabel,
  ReceiptInfoLabel,
} from './receipt-head-info.styled';
import { IOrderHeaderProps } from './types';

type OrderInfoRowProps = {
  a11yLabel: string;
  label: React.ReactNode;
  detail: React.ReactNode;
  style?: StyleProp<ViewStyle>;
};

const OrderInfoRow: FC<OrderInfoRowProps> = ({ a11yLabel, label, detail, style }) => (
  <ReceiptInfo accessible accessibilityLabel={a11yLabel} style={style}>
    <ReceiptInfoLabel>{label}</ReceiptInfoLabel>
    <ReceiptInfoDetail>{detail}</ReceiptInfoDetail>
  </ReceiptInfo>
);

const ReceiptHeadInfo: FC<React.PropsWithChildren<IOrderHeaderProps>> = ({ order }) => {
  const { formatMessage } = useIntl();
  const { language } = useLocale();
  const { pickupDate } = useOrderTimedFire({ serverOrder: order });
  const { formattedDate, formattedTime } = useMemo(() => {
    const orderDate = new Date(order.createdAt);
    const formattedOrderDate = orderDate.toLocaleString(formatMessage({ id: 'localeDateTime' }), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const formattedOrderTime = orderDate.toLocaleString('en-us', {
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
    });

    return { formattedDate: formattedOrderDate, formattedTime: formattedOrderTime };
  }, [formatMessage, order.createdAt]);

  const { storeAddress, serviceMode } = order.cart;
  const orderNumber = useOrderNumber(order);
  const localizedServiceMode = getLocalizedServiceMode(serviceMode, formatMessage);
  const pickupDateFormatted = format(pickupDate, 'PP - h:mm a', language);

  const addressAccessibilityText = useMemo(() => {
    if (!storeAddress) return '';
    const unitLine = storeAddress.addressLine2
      ? formatMessage({ id: 'unitNumber' }, { unitNumber: storeAddress.addressLine2 })
      : '';
    return `${storeAddress.addressLine1} \n${unitLine} \n${storeAddress.city}, ${storeAddress.state} \n${storeAddress.zip}`;
  }, [formatMessage, storeAddress]);

  return (
    <ReceiptHeadInfoContainer>
      <ReceiptInfo
        accessible
        accessibilityLabel={`${formatMessage({ id: 'orderDate' })}: ${formattedDate}`}
      >
        <ReceiptDate>{formattedDate}</ReceiptDate>
      </ReceiptInfo>
      {orderNumber ? (
        <OrderInfoRow
          a11yLabel={`${formatMessage({ id: 'orderNumber' })}: ${orderNumber}`}
          label={`${formatMessage({ id: 'orderNumber' })}:`}
          detail={<ReceiptInfoDetailLabel>{orderNumber}</ReceiptInfoDetailLabel>}
        />
      ) : null}
      <OrderInfoRow
        a11yLabel={`${formatMessage({ id: 'orderTime' })}: ${formattedTime}`}
        label={`${formatMessage({ id: 'orderTime' })}:`}
        detail={<ReceiptInfoDetailLabel>{formattedTime}</ReceiptInfoDetailLabel>}
      />
      <OrderInfoRow
        a11yLabel={formatMessage({ id: 'method' }, { serviceMode: localizedServiceMode })}
        label={formatMessage(
          { id: 'method' },
          {
            serviceMode: '', // set service mode below for proper formatting in grid
          }
        )}
        detail={<ReceiptInfoDetailLabel>{localizedServiceMode}</ReceiptInfoDetailLabel>}
      />
      {!!order.fireOrderIn && (
        <OrderInfoRow
          a11yLabel={`${formatMessage({ id: 'pickUpTime' })}: ${pickupDateFormatted}`}
          label={`${formatMessage({ id: 'pickUpTime' })}:`}
          detail={<ReceiptInfoDetailLabel>{pickupDateFormatted}</ReceiptInfoDetailLabel>}
        />
      )}
      {storeAddress && (
        <OrderInfoRow
          a11yLabel={`${formatMessage({ id: 'location' })}: ${addressAccessibilityText}`}
          label={`${formatMessage({ id: 'location' })}:`}
          style={{ alignItems: 'flex-start' }}
          detail={
            <ReceiptInfoDetailLabel testID="receipt-address">
              {`${storeAddress.addressLine1} \n`}
              {storeAddress.addressLine2 &&
                `${formatMessage(
                  { id: 'unitNumber' },
                  { unitNumber: storeAddress.addressLine2 }
                )} \n`}
              {`${storeAddress.city}, ${storeAddress.state}\n`}
              {`${storeAddress.zip}`}
            </ReceiptInfoDetailLabel>
          }
        />
      )}
    </ReceiptHeadInfoContainer>
  );
};

export default ReceiptHeadInfo;
